import { apiClient } from '../client';
import { ENDPOINTS } from '../endpoints';

// --- Types ---

// KX-33 / T-26: supported router vendors for the BYO flow. Persisted on the
// registration at step 1 and used by the connectivity probe to pick an adapter.
export type RouterVendor = 'omada' | 'openwrt' | 'routeros' | 'generic';
export const ROUTER_VENDORS: RouterVendor[] = ['omada', 'openwrt', 'routeros', 'generic'];

export interface Step1Data {
  deviceBrand: string;
  deviceModel: string;
  macAddress: string;
  serialNumber: string;
  locationAddress?: string; // Added to capture location from Step 1
  // Required at registration so the kit can be approved: the ISO country_code
  // decides the node's currency and plans, and the coordinates place the node.
  countryCode: string;      // 2-letter ISO code, e.g. "NG"
  latitude: number;         // real latitude, e.g. 6.5244
  longitude: number;        // real longitude, e.g. 3.3792
  // Optional today (backend won't break without it) but the picker should
  // always send the operator's selection.
  vendor?: RouterVendor;
}

// KX-33 / T-26: connectivity probe result.
export interface ConnectivityCheckResult {
  reachable: boolean;       // success state → "we can see your router"
  vendor?: RouterVendor;
  supported: boolean;       // false → no adapter for this vendor yet (e.g. routeros)
  detail?: Record<string, unknown> | null;
  error?: string | null;
}

export interface Step1Response {
  success: boolean;
  registrationId: string;
  message: string;
  nextStep: string;
}

export interface UploadDocumentResponse {
  success: boolean;
  documentUrl: string;
  documentId: string;
  documentType: string;
  message: string;
}

export interface Step2Data {
  registrationId: string;
  fullName: string;
  governmentIdType: string;
  idNumber: string;
  documents: {
    idFrontUrl: string;
    idBackUrl: string;
    proofOfAddressUrl: string; // Ensure this matches component state
    selfieUrl: string;
  };
}

export interface Step3Data {
  registrationId: string;
  paymentMethod: 'stripe' | 'flutterwave' | 'ton';
  paymentDetails: {
    amount: number;
    currency: string;
  };
}

export interface Step3Response {
  success: boolean;
  kitId: string;
  status: string;
  paymentStatus: string;
  message: string;
}

// Added for the Review Step (Step 3)
export interface RegistrationDetails extends Step1Data, Omit<Step2Data, 'registrationId'> {
  kitType: 'basic' | 'advanced' | 'premium';
  status: string;
}

export interface RegistrationStatus {
  success: boolean;
  registrationId: string;
  currentStep: number;
  status: string;
  completedSteps: number[];
  kitId?: string;
}

// --- Register Kit Service ---
export const registerKitService = {
  // Step 1: Register device details
  registerStep1: async (data: Step1Data): Promise<Step1Response> => {
    const response = await apiClient.post<Step1Response>(
      ENDPOINTS.REGISTER_KIT.STEP1,
      data
    );
    return response.data;
  },

  // Upload document (KYC documents)
  uploadDocument: async (
    registrationId: string,
    documentType: 'id_front' | 'id_back' | 'proof_of_address' | 'selfie',
    file: File
  ): Promise<UploadDocumentResponse> => {
    const formData = new FormData();
    formData.append('registrationId', registrationId);
    formData.append('documentType', documentType);
    formData.append('file', file);

    const response = await apiClient.post<UploadDocumentResponse>(
      ENDPOINTS.REGISTER_KIT.UPLOAD_DOCUMENT,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },

  // Step 2: Submit KYC data
  registerStep2: async (data: Step2Data) => {
    const response = await apiClient.post(
      ENDPOINTS.REGISTER_KIT.STEP2,
      data
    );
    return response.data;
  },

  // NEW: Get Full Details for Step 3 Review
  // This is crucial so Step 3 can show MAC address, Name, and Location
  getRegistrationDetails: async (registrationId: string): Promise<{ data: RegistrationDetails }> => {
    const response = await apiClient.get<{ data: RegistrationDetails }>(
      `${ENDPOINTS.REGISTER_KIT.STATUS(registrationId)}/details` 
      // Note: Adjust endpoint path based on your backend structure
    );
    return response.data;
  },

  // Step 3: Payment & Finalize
  registerStep3: async (data: Step3Data): Promise<Step3Response> => {
    const response = await apiClient.post<Step3Response>(
      ENDPOINTS.REGISTER_KIT.STEP3,
      data
    );
    return response.data;
  },

  submitForApproval: async (registrationId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/registerkit/submit-approval', { registrationId });
    return response.data;
  },

  // KX-33 / T-26: probe the operator's router. A false `reachable`/`supported`
  // still returns HTTP 200 — callers must inspect the payload, not catch.
  connectivityCheck: async (registrationId: string): Promise<ConnectivityCheckResult> => {
    const response = await apiClient.post<{ success: boolean; data: ConnectivityCheckResult }>(
      ENDPOINTS.REGISTER_KIT.CONNECTIVITY_CHECK,
      { registrationId }
    );
    return response.data.data;
  },

  // Helper: Get registration status
  getRegistrationStatus: async (registrationId: string): Promise<RegistrationStatus> => {
    const response = await apiClient.get<RegistrationStatus>(
      ENDPOINTS.REGISTER_KIT.STATUS(registrationId)
    );
    return response.data;
  },

};

export default registerKitService;
